// import { ZoneContextManager } from '@opentelemetry/context-zone';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { B3Propagator } from '@opentelemetry/propagator-b3';
import { ConsoleSpanExporter } from '@opentelemetry/sdk-trace-base';
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import type { Span } from '@opentelemetry/api';
import { Config } from '@packages/shared/interfaces';
import { AppEnvironmentService } from '@packages/shared/core/common/services';
import { shouldSucceedWithProbability } from '@packages/shared/core/common/utils/number/shouldSucceedWithProbability';
import { Resource, SpanStatusCode } from '../entities';
import { ObservabilitySystem } from '../interfaces';
import { OTelObservabilitySystem } from './OTelObservabilitySystem';
import { ObservabilitySystemWithSplitId } from './ObservabilitySystemWithSplitId';

export class OTelObservabilitySystemBuilder {
  private constructor(private observabilitySystem: ObservabilitySystem) {}

  static create(config: Config) {
    return new OTelObservabilitySystemBuilder(new OTelObservabilitySystem(config));
  }

  addBrowserTraceProvider(config: Partial<{ resource: Resource }> = {}): this {
    const tracerProvider = new WebTracerProvider(config);

    tracerProvider.register({
      // contextManager: new ZoneContextManager,
      propagator: new B3Propagator(),
    });

    this.observabilitySystem.setGlobalTracerProvider(tracerProvider);

    return this;
  }

  withSplitId(appEnvironment: AppEnvironmentService): this {
    this.observabilitySystem = new ObservabilitySystemWithSplitId(appEnvironment, this.observabilitySystem);

    return this;
  }

  withConsoleSpanExporter(): this {
    this.observabilitySystem.addSpanProcessor(new BatchSpanProcessor(new ConsoleSpanExporter()));

    return this;
  }

  withHttpSpanExporter(config: { url: string; headers: Record<string, unknown> }): this {
    this.observabilitySystem.addSpanProcessor(new BatchSpanProcessor(new OTLPTraceExporter(config)));

    return this;
  }

  getResult(): ObservabilitySystem {
    return this.observabilitySystem;
  }

  private addCustomAttributesToInstrumentation(span: Span, config: Partial<{ spanRate: number }> = {}) {
    const { spanRate = 1 } = config;
    // Skip sending in n percent of cases where n is (100% - spanRate * 10)
    if (!shouldSucceedWithProbability(spanRate)) {
      span.setStatus({ code: SpanStatusCode.OK });
    }

    const attributes = this.observabilitySystem.getAttributes();
    span.setAttributes({ ...attributes });
  }
}
